import { useState } from 'react'
import { ImagePlus, Link2, Upload } from 'lucide-react'
import { prepareImage, previewOf, type PreparedImage } from '../lib/images'
import { ImageLinkInput } from './ImageLinkInput'

type Props = {
  label: string
  imageUrl: string | null
  // The link the image came from, shown in link mode; null once uploaded.
  linkValue: string | null
  disabled?: boolean
  // Prepared from a link (with that link) or a file, previewed locally; the
  // editor sends it to the Guve when it saves.
  onImported: (previewUrl: string, link: string | null, prepared: PreparedImage) => void
  onPendingChange: (pending: boolean) => void
  onError: (message: string) => void
}

// A place's or producer's photo: a link or a file, prepared in the browser and
// stored in the Guve only on save (PRD.md, item 2).
export function ImageField({ label, imageUrl, linkValue, disabled, onImported, onPendingChange, onError }: Props) {
  const [mode, setMode] = useState<'link' | 'upload'>(linkValue || !imageUrl ? 'link' : 'upload')
  const [preparing, setPreparing] = useState(false)
  const [fileLabel, setFileLabel] = useState('')

  const pick = async (file: File | undefined) => {
    if (!file) return
    setPreparing(true)
    onPendingChange(true)
    try {
      const prepared = await prepareImage(file)
      onImported(await previewOf(prepared), null, prepared)
      setFileLabel(`${prepared.label} · vai para o Guve quando você salvar`)
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível preparar a imagem.')
    } finally {
      setPreparing(false)
      onPendingChange(false)
    }
  }

  return (
    <div className="field field--wide"><span>{label}</span><div className="image-editor">
      <div className="image-editor__preview">{imageUrl ? <img src={imageUrl} alt={`Prévia: ${label}`} /> : <ImagePlus size={26} />}</div>
      <div className="image-editor__controls">
        <div className="mode-toggle">
          <button type="button" disabled={disabled} className={mode === 'link' ? 'is-active' : ''} onClick={() => setMode('link')}><Link2 size={15} /> Usar link</button>
          <button type="button" disabled={disabled} className={mode === 'upload' ? 'is-active' : ''} onClick={() => { setMode('upload'); onPendingChange(false) }}><Upload size={15} /> Enviar arquivo</button>
        </div>
        {mode === 'link'
          ? <ImageLinkInput label={label} link={linkValue} imageUrl={imageUrl} disabled={disabled} onImported={onImported} onPendingChange={onPendingChange} onError={onError} />
          : <label className="upload-drop"><Upload size={18} /><span>{preparing ? 'Preparando a imagem…' : fileLabel || (imageUrl ? 'Imagem armazenada. Escolha outra para substituir.' : 'JPG, PNG ou WEBP · até 8 MB')}</span><input type="file" accept="image/jpeg,image/png,image/webp" disabled={disabled || preparing} onChange={(event) => void pick(event.target.files?.[0])} /></label>}
      </div>
    </div></div>
  )
}
